import { useState, useEffect } from 'react';

const API_BASE_URL = 'https://portalapply.online';

export const useAnalyticsSummary = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    const fetchAnalyticsSummary = async () => {
      try {
        setIsLoading(true);
        setIsError(false);

        // Fetch market summary data
        console.log('🔍 Fetching market summary from:', `${API_BASE_URL}/api/v1/market/summary`);
        const marketResponse = await fetch(`${API_BASE_URL}/api/v1/market/summary`);
        const marketData = await marketResponse.json();
        console.log('📊 Market summary response:', marketData);
        console.log('📊 Market summary keys:', Object.keys(marketData || {}));

        // Fetch active listings count (same as analytics overview)
        let activeListingsCount = 0;
        try {
          console.log('🔍 Fetching active listings...');
          let allListings = [];
          let hasMore = true;
          let nextCursor = null;
          let pageCount = 0;

          while (hasMore) {
            pageCount++;
            const url = nextCursor 
              ? `${API_BASE_URL}/api/v1/listings/active?limit=1000&cursor=${nextCursor}`
              : `${API_BASE_URL}/api/v1/listings/active?limit=1000`;
            
            console.log(`📄 Fetching active listings page ${pageCount}:`, url);
            const response = await fetch(url);
            if (!response.ok) {
              console.error('❌ Active listings fetch failed:', response.status, response.statusText);
              break;
            }
            
            const result = await response.json();
            console.log(`📊 Active listings page ${pageCount} response:`, {
              itemsCount: result.items?.length || 0,
              hasMore: result.hasMore,
              nextCursor: result.nextCursor,
              total: result.total
            });
            
            if (result.items && Array.isArray(result.items)) {
              allListings = [...allListings, ...result.items];
            }
            
            hasMore = result.hasMore || false;
            nextCursor = result.nextCursor || null;
            activeListingsCount = result.total || allListings.length;
          }
          
          console.log('✅ Active listings fetch complete:', {
            totalPages: pageCount,
            totalItems: allListings.length,
            activeListingsCount: activeListingsCount
          });
        } catch (error) {
          console.error('❌ Error fetching active listings:', error);
          // Fallback to market summary listings count
          activeListingsCount = marketData?.listings ? 
            Object.values(marketData.listings).reduce((total, period) => total + (period?.count || 0), 0) : 0;
          console.log('🔄 Using fallback active listings count:', activeListingsCount);
        }

        // Fetch holders summary
        const holdersResponse = await fetch(`${API_BASE_URL}/api/v1/holders/summary`);
        const holdersData = await holdersResponse.json();

        // Calculate average price from total volume and total sales
        const totalVolume = marketData?.volume?.total_xtz || 0;
        const totalSales = marketData?.volume?.total_sales || 0;
        const avgPrice = totalSales > 0 ? (totalVolume / totalSales) : 0;
        
        console.log('💰 Volume and Sales data:', {
          totalVolume: totalVolume,
          totalSales: totalSales,
          avgPrice: avgPrice,
          totalVolumeTez: totalVolume.toFixed(2),
          avgPriceTez: avgPrice.toFixed(2)
        });

        // Process the data for summary display
        const summaryData = {
          items: 5000, // Total collection size
          floorPrice: marketData?.floor?.price ? (marketData.floor.price / 1000000).toFixed(2) : '0',
          totalVolume: totalVolume.toFixed(2), // Already in XTZ
          volume24h: marketData?.trades?.["24h"] ? 
            (marketData.trades["24h"].events?.reduce((total, event) => total + (event.sales?.price || 0), 0) / 1000000).toFixed(2) : '0',
          owners: holdersData?.unique || 0,
          forSale: activeListingsCount, // Use active listings count
          avgPrice: avgPrice.toFixed(2), // Already calculated in XTZ
          sales24h: marketData?.trades?.["24h"] ? 
            (marketData.trades["24h"].events?.length || 0) : 0
        };

        console.log('📈 Final summary data:', summaryData);
        console.log('📈 Active listings count:', activeListingsCount);
        console.log('📈 Holders data:', holdersData);

        setData(summaryData);
      } catch (error) {
        console.error('Error fetching analytics summary:', error);
        setIsError(true);
        // Set fallback data
        setData({
          items: 5000,
          floorPrice: '0',
          totalVolume: '0',
          volume24h: '0',
          owners: 0,
          forSale: 0,
          avgPrice: '0',
          sales24h: 0
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalyticsSummary();
  }, []);

  return { data, isLoading, isError };
};
